import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { map, take } from 'rxjs/operators';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';

export const authGuard: CanActivateFn = (route, state) => {
  const auth = inject(Auth);
  const authService = inject(AuthService);
  const router = inject(Router);

  return new Promise<boolean>((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      
      if (user) {
        // Check if route requires specific role
        const requiredRole = route.data['role'];
        if (requiredRole) {
          const userProfile = authService.userProfile();
          if (userProfile && userProfile.role === requiredRole) {
            resolve(true);
          } else {
            router.navigate(['/unauthorized']);
            resolve(false);
          }
        } else {
          resolve(true);
        }
      } else {
        router.navigate(['/'], {
          queryParams: { returnUrl: state.url, showLogin: true }
        });
        resolve(false);
      }
    });
  });
};

export const adminGuard: CanActivateFn = (route, state) => {
  const auth = inject(Auth);
  const authService = inject(AuthService);
  const router = inject(Router);

  return new Promise<boolean>((resolve) => {
    // First check if auth is still loading
    if (authService.loading()) {
      // Wait for auth state to be determined
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();

        if (user) {
          // Refresh token to get latest custom claims
          await authService.refreshToken();

          // Load user profile if not already loaded
          if (!authService.userProfile()) {
            await authService.loadUserProfile(user.uid);
          }

          // Check using the isAdmin method which checks both claims and profile
          if (authService.isAdmin()) {
            resolve(true);
          } else {
            router.navigate(['/unauthorized']);
            resolve(false);
          }
        } else {
          router.navigate(['/'], {
            queryParams: { returnUrl: state.url, showLogin: true }
          });
          resolve(false);
        }
      });
    } else {
      // Auth is already loaded, check immediately
      // Use the isAdmin method which checks both claims and profile
      if (authService.isAdmin()) {
        resolve(true);
      } else {
        // Check if user is authenticated but not admin
        if (authService.currentUser()) {
          router.navigate(['/unauthorized']);
        } else {
          router.navigate(['/'], {
            queryParams: { returnUrl: state.url, showLogin: true }
          });
        }
        resolve(false);
      }
    }
  });
};

export const roleGuard: CanActivateFn = (route, state) => {
  const auth = inject(Auth);
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredRoles = route.data['roles'] as Array<'ADMIN' | 'PHYSICIAN' | 'VIEWER'>;

  if (!requiredRoles || requiredRoles.length === 0) {
    return true; // No specific roles required
  }

  return new Promise<boolean>((resolve) => {
    const checkRole = () => {
      // Check if user has any of the required roles
      const hasRequiredRole = requiredRoles.some(role => authService.hasRole(role));

      if (hasRequiredRole) {
        resolve(true);
      } else {
        router.navigate(['/unauthorized']);
        resolve(false);
      }
    };

    if (authService.loading()) {
      // Wait for auth state to be determined
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();

        if (user) {
          // Refresh token to get latest custom claims
          await authService.refreshToken();

          // Load user profile if not already loaded
          if (!authService.userProfile()) {
            await authService.loadUserProfile(user.uid);
          }

          checkRole();
        } else {
          router.navigate(['/'], {
            queryParams: { returnUrl: state.url, showLogin: true }
          });
          resolve(false);
        }
      });
    } else {
      if (authService.currentUser()) {
        checkRole();
      } else {
        router.navigate(['/login'], {
          queryParams: { returnUrl: state.url }
        });
        resolve(false);
      }
    }
  });
};

export const publicGuard: CanActivateFn = (route, state) => {
  const auth = inject(Auth);
  const router = inject(Router);

  return new Promise<boolean>((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();

      if (user) {
        router.navigate(['/patients']);
        resolve(false);
      } else {
        resolve(true);
      }
    });
  });
};

/**
 * Data Recovery Guard
 * Restricts access to data recovery tool to specific authorized users only:
 * - Mehmet Ali Şekeroğlu (msekeroglu@yahoo.com)
 * - Süleyman Kaynak (skaynak@retina-gm.com)
 * - FARTURK System Admin (registry@farturk.com)
 */
export const dataRecoveryGuard: CanActivateFn = (route, state) => {
  const auth = inject(Auth);
  const authService = inject(AuthService);
  const router = inject(Router);

  return new Promise<boolean>((resolve) => {
    const checkAccess = () => {
      if (authService.canAccessDataRecovery()) {
        resolve(true);
      } else {
        // User is authenticated but not authorized for data recovery
        router.navigate(['/unauthorized']);
        resolve(false);
      }
    };

    if (authService.loading()) {
      // Wait for auth state to be determined
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        unsubscribe();

        if (user) {
          // Ensure user profile is loaded
          if (!authService.userProfile()) {
            await authService.loadUserProfile(user.uid);
          }

          checkAccess();
        } else {
          // User not authenticated, redirect to login
          router.navigate(['/'], {
            queryParams: { returnUrl: state.url, showLogin: true }
          });
          resolve(false);
        }
      });
    } else {
      // Auth is already loaded, check immediately
      if (authService.currentUser()) {
        checkAccess();
      } else {
        // User not authenticated, redirect to login
        router.navigate(['/'], {
          queryParams: { returnUrl: state.url, showLogin: true }
        });
        resolve(false);
      }
    }
  });
};